import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { UfSelect } from "@/shared/components/UfSelect";
import { GenderType, PersonType } from "@/shared/types/enums";
import {
  useWatch,
  type UseFormReturn as UseFormReturnType,
} from "react-hook-form";
import { memo } from "react";
import { formatDocument, formatPhone } from "@/shared/lib/formatters";
import { normalizeDocumentInput } from "@/shared/lib/document";

interface ClientForm {
  personType: PersonType;
  name: string;
  document: string;
  gender?: GenderType | "";
  phone: string;
  email?: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  state: string;
  postalCode: string;
}

interface ClientFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  loading: boolean;
  form: UseFormReturnType<ClientForm>;
  onClose: () => void;
  onSubmit: (data: ClientForm) => void;
}

export const ClientFormDialog = memo(function ClientFormDialog({
  open,
  mode,
  loading,
  form,
  onClose,
  onSubmit,
}: ClientFormDialogProps) {
  // `useWatch` (e não `form.watch`) é necessário aqui: o `useForm` vive na
  // página pai e o form chega por prop. `form.watch()` só re-renderiza o
  // componente que chamou `useForm`; `useWatch` cria a própria subscription
  // neste componente, garantindo que o dialog reaja à troca de tipo.
  const personType = useWatch({ control: form.control, name: "personType" });
  const isJuridica = personType === PersonType.JURIDICA;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Cliente" : "Editar Cliente"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Preencha os dados do novo cliente"
              : "Atualize as informações do cliente"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="personType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de pessoa *</FormLabel>
                  <FormControl>
                    <select
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value as PersonType;
                        field.onChange(value);
                        if (value === PersonType.JURIDICA) {
                          form.setValue("gender", "");
                        } else if (!form.getValues("gender")) {
                          form.setValue("gender", GenderType.MALE);
                        }
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:max-w-xs"
                    >
                      <option value={PersonType.FISICA}>
                        Pessoa Física (CPF)
                      </option>
                      <option value={PersonType.JURIDICA}>
                        Pessoa Jurídica (CNPJ)
                      </option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isJuridica ? "Razão Social *" : "Nome *"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isJuridica ? "Manicorp Trajes LTDA" : "João da Silva"
                        }
                        maxLength={150}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isJuridica ? "CNPJ *" : "CPF *"}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isJuridica ? "11.222.333/0001-81" : "123.456.789-00"
                        }
                        value={field.value ? formatDocument(field.value) : ""}
                        onChange={(e) =>
                          field.onChange(
                            normalizeDocumentInput(e.target.value, isJuridica),
                          )
                        }
                        maxLength={18}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {!isJuridica && (
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gênero *</FormLabel>
                      <FormControl>
                        <select
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value={GenderType.MALE}>Masculino</option>
                          <option value={GenderType.FEMALE}>Feminino</option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="(11) 99999-9999"
                        value={
                          field.value
                            ? field.value.length >= 10
                              ? formatPhone(field.value)
                              : field.value
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 11) {
                            field.onChange(value);
                          }
                        }}
                        maxLength={15}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="joao@email.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-4">Endereço</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Rua *</FormLabel>
                        <FormControl>
                          <Input placeholder="Av. Paulista" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número *</FormLabel>
                        <FormControl>
                          <Input placeholder="123" maxLength={10} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="complement"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento *</FormLabel>
                        <FormControl>
                          <Input placeholder="Apto 101" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="neighborhood"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bairro *</FormLabel>
                        <FormControl>
                          <Input placeholder="Bela Vista" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estado *</FormLabel>
                        <FormControl>
                          <UfSelect
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CEP *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="01310100"
                            maxLength={8}
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : mode === "create"
                    ? "Criar Cliente"
                    : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
